import type { FieldValues, UseFormTrigger } from 'react-hook-form';

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { useAnalyticsContext } from '@/shared/providers/analytics';

export type FormStep<TFieldValues extends FieldValues = FieldValues> = {
  id: string;
  label: string;
  fields?: (keyof TFieldValues)[];
};

type UseMultiStepFormOptions<TFieldValues extends FieldValues = FieldValues> = {
  steps: FormStep<TFieldValues>[];
  trigger: UseFormTrigger<TFieldValues>;
  analyticsEventName: string;
  onStepValidationComplete?: (step: number, isValid: boolean) => void;
  completedSteps?: Set<number>;
};

type UseMultiStepFormReturn = {
  activeStep: number;
  previousStep: number;
  setActiveStep: (step: number) => void;
  handleDirectStepChange: (step: number) => Promise<void>;
  handleNext: (e?: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
  handleBack: (e?: React.MouseEvent<HTMLButtonElement>) => Promise<void>;
  isFirstStep: boolean;
  isLastStep: boolean;
};

/**
 * A reusable hook for managing multi-step form state and navigation.
 *
 * @example
 * ```tsx
 * const { activeStep, handleNext, handleBack, ... } = useMultiStepForm({
 *   steps: formSteps,
 *   trigger: form.trigger,
 *   analyticsEventName: 'Create User Form Step Navigation',
 * });
 * ```
 */
export const useMultiStepForm = <TFieldValues extends FieldValues = FieldValues>({
  steps,
  trigger,
  analyticsEventName,
  onStepValidationComplete,
  completedSteps,
}: UseMultiStepFormOptions<TFieldValues>): UseMultiStepFormReturn => {
  const { track } = useAnalyticsContext();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [previousStep, setPreviousStep] = useState(0);

  /**
   * Only runs once on mount to change the URL hash of the first step.
   * After that, everything is handled by the trackStepChange function.
   */
  useEffect(() => {
    if (steps.length > 0) {
      navigate({ hash: `#${steps[0].id}` }, { replace: true });
    }
  }, []);

  /**
   * Page events get tracked automatically in `SiteWrapper.tsx` with its
   * corresponding hash after updating it here. No need to retrigger the event.
   */
  const trackStepChange = useCallback(
    (step: number) => {
      if (step < 0 || step >= steps.length) {
        return;
      }
      navigate({ hash: `#${steps[step].id}` }, { replace: true });
      track(analyticsEventName, { step: steps[step].id });
    },
    [track, navigate, steps, analyticsEventName],
  );

  /**
   * Handles direct step changes from the stepper component.
   * Allows navigating to:
   * - Any previous step (step < activeStep)
   * - Any completed step (even if ahead of activeStep)
   * Validates current step before leaving to update completion state.
   */
  const handleDirectStepChange = useCallback(
    async (step: number) => {
      if (step < 0 || step >= steps.length) {
        return;
      }

      // Block navigation to incomplete future steps
      const isGoingForward = step > activeStep;
      const isTargetCompleted = completedSteps?.has(step);
      if (isGoingForward && !isTargetCompleted) {
        return;
      }

      // Validate current step before leaving to update its completion state
      const currentStep = steps[activeStep];
      const isValid = await trigger(currentStep.fields as any, {
        shouldFocus: false,
      });
      onStepValidationComplete?.(activeStep, isValid);

      trackStepChange(step);
      setPreviousStep(activeStep);
      setActiveStep(step);
    },
    [activeStep, trackStepChange, steps, trigger, onStepValidationComplete, completedSteps],
  );

  /**
   * Handles going back to the previous step.
   * Validates current step before leaving to update completion state.
   */
  const handleBack = useCallback(
    async (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.preventDefault();
      if (activeStep == 0) {
        navigate(-1);
      }
      if (activeStep <= 0) {
        return;
      }

      // Validate current step before leaving to update its completion state
      const currentStep = steps[activeStep];
      const isValid = await trigger(currentStep.fields as any, {
        shouldFocus: false,
      });
      onStepValidationComplete?.(activeStep, isValid);

      trackStepChange(activeStep - 1);
      setPreviousStep(activeStep);
      setActiveStep(activeStep - 1);
    },
    [activeStep, trackStepChange, steps, trigger, onStepValidationComplete],
  );

  /**
   * Handles going forward to the next step with validation.
   */
  const handleNext = useCallback(
    async (e?: React.MouseEvent<HTMLButtonElement>) => {
      e?.preventDefault();
      if (activeStep === steps.length - 1) {
        return;
      }

      const currentStep = steps[activeStep];
      const isValid = await trigger(currentStep.fields as any, {
        shouldFocus: true,
      });

      // Call optional callback with validation result
      onStepValidationComplete?.(activeStep, isValid);

      if (isValid) {
        trackStepChange(activeStep + 1);
        setPreviousStep(activeStep);
        setActiveStep(activeStep + 1);
      }
    },
    [activeStep, steps, trigger, trackStepChange, onStepValidationComplete],
  );

  return {
    activeStep,
    previousStep,
    setActiveStep,
    handleDirectStepChange,
    handleNext,
    handleBack,
    isFirstStep: activeStep === 0,
    isLastStep: steps.length === 0 ? true : activeStep === steps.length - 1,
  };
};
